import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Target, Users, Award, Heart, Lightbulb, Globe,
  CheckCircle
} from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'Democratizing AI education and making it accessible to everyone, regardless of background.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Building a supportive community where students learn from each other and grow together.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Delivering world-class curriculum designed and taught by industry experts.'
    },
    {
      icon: Heart,
      title: 'Student Success',
      description: 'Your success is our success. We measure ourselves by your career outcomes.'
    }
  ];

  const milestones = [
    { year: '2020', event: 'WSafe AI ML founded with a mission to democratize AI education' },
    { year: '2021', event: 'Launched first comprehensive AI & ML curriculum' },
    { year: '2022', event: 'Reached 5,000 students across 50+ countries' },
    { year: '2023', event: '94% job placement rate achieved' },
    { year: '2024', event: 'Expanded to 12,500+ students with enterprise partnerships' },
    { year: '2026', event: 'Launched 2026 curriculum with latest AI advancements' }
  ];

  return (
    <div className="page about-page">
      <Header onEnrollClick={() => window.location.href = '/pricing'} />
      
      <main className="page-content">
        {/* Hero */}
        <section className="page-hero about-hero">
          <div className="container">
            <span className="section-tag">Our Story</span>
            <h1 className="page-title">About WSafe AI ML</h1>
            <p className="page-subtitle">
              Empowering the next generation of AI professionals with world-class education
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="mission-section">
          <div className="container">
            <div className="mission-content">
              <div className="mission-text">
                <h2>Our Mission</h2>
                <p className="mission-statement">
                  We believe AI will shape the future of every industry. Our mission is to prepare 
                  ambitious professionals with the skills, knowledge, and confidence to lead this transformation.
                </p>
                <p>
                  Founded by AI researchers and industry practitioners, WSafe AI ML combines academic rigor 
                  with practical, hands-on learning. We don't just teach theory—we prepare you to build 
                  real AI systems that solve real problems.
                </p>
              </div>
              <div className="mission-stats">
                <div className="mission-stat">
                  <span className="stat-value">12,500+</span>
                  <span className="stat-label">Students Worldwide</span>
                </div>
                <div className="mission-stat">
                  <span className="stat-value">94%</span>
                  <span className="stat-label">Job Placement Rate</span>
                </div>
                <div className="mission-stat">
                  <span className="stat-value">50+</span>
                  <span className="stat-label">Countries</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="values-section">
          <div className="container">
            <h2 className="section-title">Our Values</h2>
            <div className="values-grid">
              {values.map((value, idx) => (
                <div key={idx} className="value-card">
                  <value.icon className="value-icon" />
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="timeline-section">
          <div className="container">
            <h2 className="section-title">Our Journey</h2>
            <div className="timeline">
              {milestones.map((milestone, idx) => (
                <div key={idx} className="timeline-item">
                  <div className="timeline-marker">
                    <CheckCircle size={20} />
                  </div>
                  <div className="timeline-content">
                    <span className="timeline-year">{milestone.year}</span>
                    <p>{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="about-cta">
          <div className="container">
            <div className="cta-card">
              <Globe className="cta-icon" />
              <h2>Join Our Global Community</h2>
              <p>Become part of a network of AI professionals transforming industries worldwide</p>
              <div className="cta-buttons">
                <Link to="/pricing">
                  <Button className="btn-primary">Start Learning</Button>
                </Link>
                <Link to="/instructors">
                  <Button className="btn-secondary">Meet Our Team</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
